import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { SuiteButton, SuiteBadge } from "@/components/suite";
import { ArrowLeft, Save, IdCard, Briefcase, PiggyBank, CalendarDays, KeyRound, Copy, ShieldOff, Plus, Pencil, X, ShieldCheck, CalendarClock, Trash2 } from "lucide-react";
import { MONTHS_IT } from "@/lib/constants";
import { RateiDetailGrid, CounterSourceBadge } from "@/components/RateiDetail";
import { formatDateIT, fmtHours } from "@/foundation/formatting";
// LEGACY — non usare. Sostituito dal nuovo `EmployeeSafetyTab` (Release 1.1.0).
// Il file `EmployeeSafetyPanel.jsx` resta a disposizione come rollback tecnico
// e verrà eliminato dopo il collaudo della 1.1.0.
import EmployeeSafetyTab from "@/components/EmployeeSafetyTab";
import { useEffectiveRole } from "@/foundation/context";
import { DurationInput, DurationLabel } from "@/foundation/duration";
import { CounterHoursInput } from "@/components/employees/CounterHoursInput";
import { AccrualDeleteDialog } from "@/components/employees/AccrualDeleteDialog";
import { CounterAdjustmentsPanel } from "@/components/employees/CounterAdjustmentsPanel";
import { formatCounterHours } from "@/lib/counterHours";

/**
 * v0.99.85+ · Modifica Dipendente su pagina dedicata `/dipendenti/:id`.
 * Modello grafico e di navigazione allineato a `CompanyEditPage.jsx`:
 *  - Header con "Torna ai dipendenti" + titolo fisso "Modifica Dipendente".
 *  - Barra schede sticky orizzontale, layout ampio.
 *  - Barra "Salva" sticky in fondo (visibile su Anagrafica/Contratto/Banca ore/Contatori).
 *  - Blocco uscita accidentale con dati non salvati (goBack + beforeunload).
 * Le regole di permessi/validazione/storicizzazione replicano 1:1 quelle del vecchio popup.
 */

const VALID_TABS = ["anagrafica", "contratto", "banca-ore", "contatori", "sicurezza", "account"];
const CESSATION_REASONS = [
  { v: "DIMISSIONI", label: "Dimissioni" },
  { v: "LICENZIAMENTO", label: "Licenziamento" },
  { v: "PENSIONAMENTO", label: "Pensionamento" },
  { v: "FINE_CONTRATTO", label: "Fine contratto" },
  { v: "ALTRO", label: "Altro" },
];

const empty = () => ({
  first_name: "", last_name: "", fiscal_code: "", email: "", matricola: "",
  hire_date: "",
  qualification: "", level: "",
  residence_address: "", residence_city: "", residence_cap: "", residence_province: "",
  phone_mobile: "",
  employment_type: "full_time",
  initial_balance_hours: 0,
  initial_balance_month: new Date().getMonth() + 1,
  initial_balance_year: new Date().getFullYear(),
  ratei_base_year: null,
  ratei_ferie_iniziale_gg: 0,
  ratei_rol_iniziale_h: 0,
  ratei_ex_fest_iniziale_h: 0,
  active: true,
  cessation_date: "",
  cessation_reason: "",
  bank_hours_enabled: false,
  bank_hours_start_date: "",
  company_id: null,
});

const currentYear = () => new Date().getFullYear();
const currentMonth = () => new Date().getMonth() + 1;

function fmtIsoIt(iso) {
  return formatDateIT(iso, "");
}

export default function EmployeeEditPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const effectiveRole = useEffectiveRole();
  const isExternalConsultant = effectiveRole === "external_consultant";
  const isEmployeeSelf = effectiveRole === "employee";

  const currentTab = useMemo(() => {
    const t = searchParams.get("tab");
    if (isEmployeeSelf && (t === "contratto" || t === "banca-ore" || t === "account")) return "anagrafica";
    return VALID_TABS.includes(t) ? t : "anagrafica";
  }, [searchParams, isEmployeeSelf]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(null);
  const [initialForm, setInitialForm] = useState(null);
  const [company, setCompany] = useState(null);          // azienda del dipendente (per bank_hours_enabled)
  const [rateiSummary, setRateiSummary] = useState(null);
  const [accrualProfiles, setAccrualProfiles] = useState([]);

  // Nuova/edit decorrenza contatori
  const emptyAccrual = () => ({
    effective_from: `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, "0")}-01`,
    vacation_monthly: 0,
    vacation_unit: "days",
    rol_monthly_hours: 0,
    ex_fest_monthly_hours: 0,
  });
  const [newAccrual, setNewAccrual] = useState(emptyAccrual());
  const [editingAccrualId, setEditingAccrualId] = useState(null);
  const [accrualBusy, setAccrualBusy] = useState(false);
  const [accrualDialogOpen, setAccrualDialogOpen] = useState(false);
  const [deletingAccrual, setDeletingAccrual] = useState(null);
  const closeAccrualDelete = useCallback(() => setDeletingAccrual(null), []);

  // Account portale
  const [account, setAccount] = useState({ has_account: false });
  const [accUsername, setAccUsername] = useState("");
  const [accPassword, setAccPassword] = useState("");
  const [accBusy, setAccBusy] = useState(false);

  const isDirty = useMemo(
    () => !!form && !!initialForm && JSON.stringify(form) !== JSON.stringify(initialForm),
    [form, initialForm]
  );

  // ============ Load ============
  const loadEmployee = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/employees/${id}`);
      const filled = { ...empty(), ...data };
      setForm(filled);
      setInitialForm(filled);
      // Azienda associata (per companyBankOff)
      if (data.company_id) {
        try {
          const c = await api.get(`/companies/${data.company_id}`);
          setCompany(c.data);
        } catch { setCompany(null); }
      }
      // Ratei summary
      api.get("/attendance/ratei-summary", {
        params: { employee_id: id, year: currentYear(), month: currentMonth() },
      }).then(({ data: r }) => setRateiSummary(r)).catch(() => setRateiSummary(null));
      // Accrual history
      api.get(`/employees/${id}/accrual-profiles`)
        .then(({ data: a }) => setAccrualProfiles(a.items || []))
        .catch(() => setAccrualProfiles([]));
      // Account portale (solo ruoli gestionali)
      if (!isEmployeeSelf && !isExternalConsultant) {
        try {
          const { data: acc } = await api.get(`/employees/${id}/account`);
          setAccount(acc); setAccUsername(acc.username || "");
        } catch { setAccount({ has_account: false }); setAccUsername(""); }
      }
    } catch (e) {
      toast.error(apiErr(e) || "Dipendente non trovato");
      navigate(isEmployeeSelf ? "/mio-profilo" : "/dipendenti", { replace: true });
    } finally { setLoading(false); }
  }, [id, navigate, isEmployeeSelf, isExternalConsultant]);

  useEffect(() => { if (id) loadEmployee(); }, [id, loadEmployee]);

  // ============ Uscita accidentale ============
  useEffect(() => {
    if (!isDirty) return;
    const handler = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [isDirty]);

  const changeTab = (t) => {
    setSearchParams((p) => { const n = new URLSearchParams(p); n.set("tab", t); return n; }, { replace: false });
  };

  const goBack = () => {
    if (isDirty) {
      const ok = window.confirm("Ci sono modifiche non salvate. Uscire senza salvare?");
      if (!ok) return;
    }
    navigate(isEmployeeSelf ? "/mio-profilo" : "/dipendenti");
  };

  // ============ Save anagrafica/contratto/banca-ore/contatori ============
  const save = async () => {
    if (!form.first_name?.trim() || !form.last_name?.trim()) {
      toast.error("Nome e cognome obbligatori");
      setSearchParams((p) => { const n = new URLSearchParams(p); n.set("tab", "anagrafica"); return n; }, { replace: true });
      return;
    }
    if (form.cessation_date && !form.cessation_reason) {
      toast.error("Motivo cessazione obbligatorio quando è impostata una data");
      setSearchParams((p) => { const n = new URLSearchParams(p); n.set("tab", "contratto"); return n; }, { replace: true });
      return;
    }
    setSaving(true);
    try {
      const payload = isEmployeeSelf
        ? {
            first_name: form.first_name, last_name: form.last_name,
            fiscal_code: form.fiscal_code || null,
            email: form.email || null,
            residence_address: form.residence_address || null,
            residence_city: form.residence_city || null,
            residence_cap: form.residence_cap || null,
            residence_province: form.residence_province || null,
            phone_mobile: form.phone_mobile || null,
          }
        : {
            first_name: form.first_name, last_name: form.last_name,
            fiscal_code: form.fiscal_code || null, email: form.email || null,
            matricola: form.matricola || null,
            hire_date: form.hire_date || null,
            qualification: form.qualification || null,
            level: form.level || null,
            residence_address: form.residence_address || null,
            residence_city: form.residence_city || null,
            residence_cap: form.residence_cap || null,
            residence_province: form.residence_province || null,
            phone_mobile: form.phone_mobile || null,
            employment_type: form.employment_type,
            initial_balance_hours: Number(form.initial_balance_hours),
            initial_balance_month: Number(form.initial_balance_month),
            initial_balance_year: Number(form.initial_balance_year),
            ratei_base_year: form.ratei_base_year ? Number(form.ratei_base_year) : null,
            ratei_ferie_iniziale_gg: Number(form.ratei_ferie_iniziale_gg || 0),
            ratei_rol_iniziale_h: Number(form.ratei_rol_iniziale_h || 0),
            ratei_ex_fest_iniziale_h: Number(form.ratei_ex_fest_iniziale_h || 0),
            cessation_date: form.cessation_date || null,
            cessation_reason: form.cessation_reason || null,
            bank_hours_enabled: !!form.bank_hours_enabled,
            bank_hours_start_date: form.bank_hours_start_date || null,
          };
      await api.patch(`/employees/${id}`, payload);
      toast.success("Dipendente aggiornato");
      // Ricarica dati persistiti dal server
      await loadEmployee();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setSaving(false); }
  };

  // ============ Accrual profiles (storicizzazione contatori) ============
  const openNewAccrualDialog = () => {
    setEditingAccrualId(null);
    setNewAccrual(emptyAccrual());
    setAccrualDialogOpen(true);
  };
  const saveAccrualProfile = async () => {
    setAccrualBusy(true);
    try {
      await api.post(`/employees/${id}/accrual-profiles`, {
        ...newAccrual,
        vacation_monthly: Number(newAccrual.vacation_monthly || 0),
        rol_monthly_hours: Number(newAccrual.rol_monthly_hours || 0),
        ex_fest_monthly_hours: Number(newAccrual.ex_fest_monthly_hours || 0),
      });
      const [{ data: history }, { data: summary }] = await Promise.all([
        api.get(`/employees/${id}/accrual-profiles`),
        api.get("/attendance/ratei-summary", { params: { employee_id: id, year: currentYear(), month: currentMonth() } }),
      ]);
      setAccrualProfiles(history.items || []); setRateiSummary(summary);
      setNewAccrual(emptyAccrual());
      setAccrualDialogOpen(false);
      toast.success("Nuova decorrenza salvata; i valori precedenti restano nello storico");
    } catch (e) {
      const firstEditable = e?.response?.data?.detail?.first_editable_month;
      if (firstEditable) setNewAccrual((f) => ({ ...f, effective_from: firstEditable }));
      toast.error(apiErr(e));
    }
    finally { setAccrualBusy(false); }
  };
  const startAccrualEdit = (row) => {
    setEditingAccrualId(row.id);
    setNewAccrual({
      effective_from: String(row.effective_from || "").slice(0, 10),
      vacation_monthly: Number(row.vacation_monthly || 0),
      vacation_unit: row.vacation_unit === "hours" ? "hours" : "days",
      rol_monthly_hours: Number(row.rol_monthly_hours || 0),
      ex_fest_monthly_hours: Number(row.ex_fest_monthly_hours || 0),
    });
    setAccrualDialogOpen(true);
  };
  const afterAccrualDeleted = async () => {
    setDeletingAccrual(null);
    const [{ data: history }, { data: summary }] = await Promise.all([
      api.get(`/employees/${id}/accrual-profiles`),
      api.get("/attendance/ratei-summary", { params: { employee_id: id, year: currentYear(), month: currentMonth() } }),
    ]);
    setAccrualProfiles(history.items || []); setRateiSummary(summary);
  };
  const openCorrectiveAccrual = (suggestion) => {
    setDeletingAccrual(null);
    setEditingAccrualId(null);
    setNewAccrual({ ...emptyAccrual(), ...suggestion });
    setAccrualDialogOpen(true);
  };
  const cancelAccrualEdit = () => {
    setEditingAccrualId(null);
    setNewAccrual(emptyAccrual());
    setAccrualDialogOpen(false);
  };
  const updateAccrualProfile = async () => {
    if (!editingAccrualId) return;
    setAccrualBusy(true);
    try {
      await api.patch(`/employees/${id}/accrual-profiles/${editingAccrualId}`, {
        ...newAccrual,
        vacation_monthly: Number(newAccrual.vacation_monthly || 0),
        rol_monthly_hours: Number(newAccrual.rol_monthly_hours || 0),
        ex_fest_monthly_hours: Number(newAccrual.ex_fest_monthly_hours || 0),
      });
      const [{ data: history }, { data: summary }] = await Promise.all([
        api.get(`/employees/${id}/accrual-profiles`),
        api.get("/attendance/ratei-summary", { params: { employee_id: id, year: currentYear(), month: currentMonth() } }),
      ]);
      setAccrualProfiles(history.items || []); setRateiSummary(summary);
      cancelAccrualEdit();
      toast.success("Decorrenza aggiornata");
    } catch (e) { toast.error(apiErr(e)); }
    finally { setAccrualBusy(false); }
  };

  // ============ Account portale ============
  const saveAccount = async () => {
    if (!form.email?.trim()) { toast.error("Inserisci prima l'email nell'Anagrafica"); return; }
    if (account.has_account && !accUsername.trim()) { toast.error("Nome utente obbligatorio"); return; }
    if (accPassword && accPassword.length < 6) { toast.error("Password troppo corta (min 6 caratteri)"); return; }
    setAccBusy(true);
    try {
      const body = { email: form.email.trim().toLowerCase(), username: accUsername.trim() || null };
      if (accPassword) body.password = accPassword;
      const { data } = await api.put(`/employees/${id}/account`, body);
      setAccount(data); setAccUsername(data.username || ""); setAccPassword("");
      toast.success(
        data.linked_existing ? "Account esistente collegato senza duplicazioni"
        : (account.has_account ? "Account aggiornato" : "Account creato")
      );
    } catch (e) { toast.error(apiErr(e)); }
    finally { setAccBusy(false); }
  };
  const deleteAccount = async () => {
    if (!account.has_account) return;
    if (!window.confirm(
      "Rimuovere l'accesso di questo dipendente al portale?\n\n" +
      "I dati anagrafici e le presenze resteranno invariati. Se l'account è " +
      "esclusivamente dipendente verrà eliminato e il nome utente tornerà " +
      "disponibile. Se possiede anche un ruolo aziendale, verrà soltanto " +
      "scollegato dal dipendente."
    )) return;
    setAccBusy(true);
    try {
      const { data } = await api.delete(`/employees/${id}/account`);
      setAccount({ has_account: false }); setAccUsername(""); setAccPassword("");
      toast.success(data.account_deleted
        ? "Account dipendente eliminato; nome utente nuovamente disponibile"
        : "Accesso dipendente scollegato; account aziendale conservato");
    } catch (e) { toast.error(apiErr(e)); }
    finally { setAccBusy(false); }
  };
  const generateRandomPassword = () => {
    const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789!#@$*";
    let p = "";
    for (let i = 0; i < 12; i++) p += chars.charAt(Math.floor(Math.random() * chars.length));
    setAccPassword(p);
    navigator.clipboard.writeText(p).catch(() => {});
    toast.success("Password generata e copiata negli appunti");
  };

  if (loading || !form) {
    return <div className="p-6 text-sm text-slate-500">Caricamento dipendente…</div>;
  }

  const companyBankOff = company && company.bank_hours_enabled === false;
  const today = new Date().toISOString().slice(0, 10);
  const showAccountTab = !isEmployeeSelf && !isExternalConsultant;
  // v0.99.85+: scheda Sicurezza (D.Lgs. 81/2008) sempre visibile (anche dipendente vede solo i propri).
  const showSafetyTab = true;
  const showSaveBar = !isExternalConsultant &&
    (currentTab === "anagrafica" || currentTab === "contratto"
      || currentTab === "banca-ore" || currentTab === "contatori");

  return (
    <div className="p-4 md:p-6 max-w-6xl mx-auto" data-testid="employee-edit-page">
      <Tabs value={currentTab} onValueChange={changeTab} className="w-full">
        {/* Header + Tabs sticky ancorati al TRUE top del contenitore di scroll.
            Compensiamo il padding del wrapper `suite-content-scroll` (p-4 md:p-6 lg:p-8)
            con top negativo + padding-top interno, cosi' il contenuto sottostante
            non passa attraverso la barra durante lo scroll. */}
        <div className="sticky -top-4 md:-top-6 lg:-top-8 z-30 -mx-4 md:-mx-6 bg-white px-4 md:px-6 pt-4 md:pt-6 lg:pt-8 pb-2 border-b border-slate-200 shadow-sm">
          {/* Header (rimane fisso insieme alle schede durante lo scroll) */}
          <div className="mb-3">
            <button
              type="button"
              onClick={goBack}
              className="text-sm text-suite-primary hover:underline inline-flex items-center gap-1 mb-1.5"
              data-testid="employee-back-btn"
            >
              <ArrowLeft size={14} /> {isEmployeeSelf ? "Torna al mio profilo" : "Torna ai dipendenti"}
            </button>
            <h1 className="text-2xl font-semibold text-slate-900 leading-tight" data-testid="employee-title">
              {isEmployeeSelf ? "Il mio profilo" : "Modifica Dipendente"}
            </h1>
            <p className="text-sm text-slate-500 mt-0.5">
              {form.last_name} {form.first_name}
              {form.matricola ? ` — Matr. ${form.matricola}` : ""}
              {company?.name ? ` · ${company.name}` : ""}
            </p>
          </div>
          <TabsList className={`w-full overflow-x-auto flex md:grid ${
              isEmployeeSelf ? "md:grid-cols-3" : (showAccountTab ? "md:grid-cols-6" : "md:grid-cols-5")
            } gap-1 justify-start`}>
            <TabsTrigger value="anagrafica" data-testid="employee-tab-anagrafica" className="whitespace-nowrap">
              <IdCard size={13} className="mr-1.5" /> Anagrafica
            </TabsTrigger>
            {!isEmployeeSelf && (
              <TabsTrigger value="contratto" data-testid="employee-tab-contratto" className="whitespace-nowrap">
                <Briefcase size={13} className="mr-1.5" /> Contratto
              </TabsTrigger>
            )}
            {!isEmployeeSelf && (
              <TabsTrigger value="banca-ore" data-testid="employee-tab-banca-ore" className="whitespace-nowrap">
                <PiggyBank size={13} className="mr-1.5" /> Banca ore
              </TabsTrigger>
            )}
            <TabsTrigger value="contatori" data-testid="employee-tab-contatori" className="whitespace-nowrap">
              <CalendarDays size={13} className="mr-1.5" /> Contatori
            </TabsTrigger>
            {showSafetyTab && (
              <TabsTrigger value="sicurezza" data-testid="employee-tab-sicurezza" className="whitespace-nowrap">
                <ShieldCheck size={13} className="mr-1.5" /> Sicurezza sul lavoro
              </TabsTrigger>
            )}
            {showAccountTab && (
              <TabsTrigger value="account" data-testid="employee-tab-account" className="whitespace-nowrap">
                <KeyRound size={13} className="mr-1.5" /> Account portale
              </TabsTrigger>
            )}
          </TabsList>
        </div>

        {/* === Anagrafica === */}
        <TabsContent value="anagrafica" className="mt-6">
          <fieldset disabled={isExternalConsultant} className="space-y-4 disabled:opacity-100">
            <div className="grid md:grid-cols-2 gap-3">
              <div><Label className="text-xs">Cognome *</Label><Input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} data-testid="emp-form-lastname" /></div>
              <div><Label className="text-xs">Nome *</Label><Input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} data-testid="emp-form-firstname" /></div>
              <div className="md:col-span-2"><Label className="text-xs">Codice fiscale</Label><Input value={form.fiscal_code || ""} onChange={(e) => setForm({ ...form, fiscal_code: e.target.value })} data-testid="emp-form-cf" className="font-mono uppercase" /></div>
              <div className="md:col-span-2">
                <Label className="text-xs">Email personale</Label>
                <Input type="email" value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="Usata per notifiche e recupero password" data-testid="emp-form-email" />
                <p className="text-[11px] text-suite-neutral-500 mt-1">
                  Non è il nome utente: l&apos;accesso avviene sempre con nome.cognome.
                </p>
              </div>
            </div>
            <div className="pt-2">
              <div className="text-[11px] uppercase tracking-widest text-suite-neutral-500 font-semibold mb-2">Residenza attuale</div>
              <div className="grid md:grid-cols-6 gap-3">
                <div className="md:col-span-4"><Label className="text-xs">Indirizzo</Label><Input value={form.residence_address || ""} onChange={(e) => setForm({ ...form, residence_address: e.target.value })} placeholder="Via / Piazza e civico" data-testid="emp-form-address" /></div>
                <div className="md:col-span-2"><Label className="text-xs">CAP</Label><Input value={form.residence_cap || ""} onChange={(e) => setForm({ ...form, residence_cap: e.target.value })} placeholder="00100" data-testid="emp-form-cap" maxLength={5} className="font-mono" /></div>
                <div className="md:col-span-4"><Label className="text-xs">Città</Label><Input value={form.residence_city || ""} onChange={(e) => setForm({ ...form, residence_city: e.target.value })} data-testid="emp-form-city" /></div>
                <div className="md:col-span-2"><Label className="text-xs">Provincia</Label><Input value={form.residence_province || ""} onChange={(e) => setForm({ ...form, residence_province: e.target.value.toUpperCase().slice(0, 2) })} placeholder="RM" data-testid="emp-form-province" maxLength={2} className="font-mono uppercase" /></div>
              </div>
            </div>
            <div className="pt-2">
              <div className="text-[11px] uppercase tracking-widest text-suite-neutral-500 font-semibold mb-2">Contatti</div>
              <div className="grid md:grid-cols-2 gap-3">
                <div><Label className="text-xs">Telefono cellulare</Label><Input type="tel" value={form.phone_mobile || ""} onChange={(e) => setForm({ ...form, phone_mobile: e.target.value })} placeholder="+39 ..." data-testid="emp-form-mobile" /></div>
              </div>
            </div>
          </fieldset>
        </TabsContent>

        {/* === Contratto === */}
        {!isEmployeeSelf && (
          <TabsContent value="contratto" className="mt-6">
            <fieldset disabled={isExternalConsultant} className="space-y-4 disabled:opacity-100">
              <div className="grid md:grid-cols-2 gap-3">
                <div><Label className="text-xs">Matricola</Label><Input value={form.matricola || ""} onChange={(e) => setForm({ ...form, matricola: e.target.value })} data-testid="emp-form-matricola" /></div>
                <div><Label className="text-xs">Data assunzione</Label><Input type="date" value={form.hire_date || ""} onChange={(e) => setForm({ ...form, hire_date: e.target.value })} data-testid="emp-form-hire-date" /></div>
                <div><Label className="text-xs">Qualifica</Label><Input value={form.qualification || ""} onChange={(e) => setForm({ ...form, qualification: e.target.value })} placeholder="es. Operaio specializzato" data-testid="emp-form-qualification" /></div>
                <div><Label className="text-xs">Livello</Label><Input value={form.level || ""} onChange={(e) => setForm({ ...form, level: e.target.value })} placeholder="es. 3° livello CCNL" data-testid="emp-form-level" /></div>
                <div>
                  <Label className="text-xs">Tipo</Label>
                  <Select value={form.employment_type} onValueChange={(v) => setForm({ ...form, employment_type: v })}>
                    <SelectTrigger data-testid="emp-form-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="full_time">Full time</SelectItem>
                      <SelectItem value="part_time">Part time</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Cessazione rapporto */}
              <div className="border-t border-suite-neutral-200 pt-3 mt-2">
                <div className="text-[10px] uppercase tracking-widest font-semibold text-suite-neutral-500 mb-2">Cessazione rapporto</div>
                <div className="grid md:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Data cessazione</Label>
                    <Input type="date" value={form.cessation_date || ""} onChange={(e) => setForm({ ...form, cessation_date: e.target.value })} data-testid="emp-form-cessation-date" />
                    <div className="text-[10px] text-suite-neutral-500 mt-1">Ultimo giorno di rapporto. Lascia vuoto se rapporto attivo.</div>
                  </div>
                  <div>
                    <Label className="text-xs">
                      Motivo cessazione {form.cessation_date && <span className="text-suite-error"> *</span>}
                    </Label>
                    <Select value={form.cessation_reason || ""} onValueChange={(v) => setForm({ ...form, cessation_reason: v })} disabled={!form.cessation_date}>
                      <SelectTrigger data-testid="emp-form-cessation-reason"><SelectValue placeholder="Seleziona motivo…" /></SelectTrigger>
                      <SelectContent>
                        {CESSATION_REASONS.map((r) => <SelectItem key={r.v} value={r.v}>{r.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <div className="text-[10px] text-suite-neutral-500 mt-1">
                      {form.cessation_date ? "Obbligatorio quando la data di cessazione è impostata." : "Attivo quando è impostata una data di cessazione."}
                    </div>
                  </div>
                </div>
                {form.cessation_date && (
                  <div className="mt-2 flex items-center justify-between gap-3 p-2 rounded-md bg-suite-neutral-50 border border-suite-neutral-200">
                    <div className="text-xs text-suite-neutral-700">
                      {new Date(form.cessation_date) >= new Date(today)
                        ? <>Cessazione <b>prevista</b> per il {fmtIsoIt(form.cessation_date)}. Il dipendente resta operativo fino a quella data.</>
                        : <>Cessazione già avvenuta il {fmtIsoIt(form.cessation_date)}. Escluso dalle elaborazioni dal giorno successivo.</>}
                    </div>
                    <SuiteButton variant="ghost" size="sm" onClick={() => setForm({ ...form, cessation_date: "", cessation_reason: "" })} data-testid="emp-form-cancel-cessation">
                      Annulla cessazione
                    </SuiteButton>
                  </div>
                )}
              </div>
            </fieldset>
          </TabsContent>
        )}

        {/* === Banca ore === */}
        {!isEmployeeSelf && (
          <TabsContent value="banca-ore" className="mt-6">
            <fieldset disabled={isExternalConsultant} className="space-y-4 disabled:opacity-100">
              <div className="px-3 py-3 rounded-md border border-suite-neutral-200 bg-suite-surface" data-testid="emp-form-bank-hours-section">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={!!form.bank_hours_enabled}
                        disabled={companyBankOff}
                        onCheckedChange={(v) => setForm({
                          ...form,
                          bank_hours_enabled: v,
                          bank_hours_start_date: v && !form.bank_hours_start_date ? today : form.bank_hours_start_date,
                        })}
                        data-testid="emp-form-bank-hours-toggle"
                      />
                      <Label className="text-sm font-semibold">Gestione banca ore</Label>
                    </div>
                    {companyBankOff ? (
                      <div className="mt-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-[11px] text-amber-900">
                        <div className="font-semibold mb-0.5">Banca ore disattivata a livello di azienda.</div>
                        <div>Per abilitarla per questo dipendente devi prima attivarla nella scheda azienda. I valori qui restano memorizzati per un&apos;eventuale riattivazione.</div>
                        {["admin", "super_admin", "client"].includes(effectiveRole) && form.company_id && (
                          <button
                            type="button"
                            className="mt-1.5 text-suite-primary hover:underline font-semibold inline-flex items-center gap-1"
                            onClick={() => {
                              if (isDirty) {
                                const ok = window.confirm("Ci sono modifiche non salvate. Uscire senza salvare?");
                                if (!ok) return;
                              }
                              navigate(`/aziende/${form.company_id}?tab=banca-ore`);
                            }}
                            data-testid="emp-goto-company-bank-hours"
                          >
                            Apri l&apos;azienda per abilitare la banca ore →
                          </button>
                        )}
                      </div>
                    ) : (
                      <p className="text-[11px] text-suite-neutral-500 mt-1">
                        Abilita solo quando previsto contrattualmente.
                      </p>
                    )}
                  </div>
                </div>
                {form.bank_hours_enabled && !companyBankOff && (
                  <div className="mt-3 grid md:grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Decorrenza banca ore</Label>
                      <Input type="date" value={form.bank_hours_start_date || ""} onChange={(e) => setForm({ ...form, bank_hours_start_date: e.target.value })} data-testid="emp-form-bank-hours-start-date" />
                      <p className="text-[10px] text-suite-neutral-400 mt-1">La banca ore è attiva solo dalle date &ge; a questa.</p>
                    </div>
                  </div>
                )}
              </div>

              <div className="text-[11px] uppercase tracking-widest text-suite-neutral-500 font-semibold mb-1">Saldo iniziale banca ore</div>
              <div className="grid md:grid-cols-3 gap-3">
                <div><DurationLabel className="text-xs" data-testid="emp-form-initial-hours">Ore</DurationLabel><DurationInput allowNegative value={form.initial_balance_hours ?? 0} onCommit={(n) => setForm((f) => ({ ...f, initial_balance_hours: n ?? 0 }))} data-testid="emp-form-initial-hours" /></div>
                <div>
                  <Label className="text-xs">Mese</Label>
                  <Select value={String(form.initial_balance_month)} onValueChange={(v) => setForm({ ...form, initial_balance_month: Number(v) })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{MONTHS_IT.map((m, i) => <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label className="text-xs">Anno</Label><Input type="number" value={form.initial_balance_year} onChange={(e) => setForm({ ...form, initial_balance_year: Number(e.target.value) })} /></div>
              </div>
            </fieldset>
          </TabsContent>
        )}

        {/* === Contatori === */}
        <TabsContent value="contatori" className="mt-6 space-y-4" data-testid="employee-ratei-tab">
          <CounterSourceBadge consolidation={rateiSummary?.consolidation} />
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
            <div>
              <div className="text-xs font-semibold text-suite-neutral-700 mb-2">Ferie</div>
              <RateiDetailGrid pool={rateiSummary?.ferie_pool} testid="employee-ratei-ferie" />
            </div>
            <div>
              <div className="text-xs font-semibold text-suite-neutral-700 mb-2">ROL / Permessi</div>
              <RateiDetailGrid pool={rateiSummary?.rol_pool} testid="employee-ratei-rol" />
            </div>
            <div>
              <div className="text-xs font-semibold text-suite-neutral-700 mb-2">Ex festività</div>
              <RateiDetailGrid pool={rateiSummary?.ex_fest_pool} testid="employee-ratei-exfest" />
            </div>
          </div>

          {!isEmployeeSelf && !isExternalConsultant && (
            <CounterAdjustmentsPanel employeeId={id} onChanged={afterAccrualDeleted} />
          )}

          {!isEmployeeSelf ? (
            <div className="rounded-lg border border-suite-neutral-200 bg-suite-neutral-50 p-4">
              <div className="mb-3">
                <div className="text-sm font-semibold text-suite-neutral-800">Valori contrattuali dei contatori</div>
                <div className="text-xs text-suite-neutral-500">Le modifiche vengono applicate cliccando “Salva modifiche” in fondo alla pagina.</div>
              </div>
              <fieldset disabled={isExternalConsultant} className="grid md:grid-cols-3 gap-3 disabled:opacity-100">
                {accrualProfiles[0]?.vacation_unit === "hours" ? (
                  <div><DurationLabel className="text-xs" data-testid="ratei-ferie-initial">Ferie residue iniziali (h)</DurationLabel><DurationInput value={form.ratei_ferie_iniziale_gg ?? 0} onCommit={(n) => setForm((f) => ({ ...f, ratei_ferie_iniziale_gg: n ?? 0 }))} data-testid="ratei-ferie-initial" /></div>
                ) : (
                  <div><Label className="text-xs">Ferie residue iniziali (gg)</Label><Input type="number" step="0.01" value={form.ratei_ferie_iniziale_gg ?? 0} onChange={(e) => setForm({ ...form, ratei_ferie_iniziale_gg: e.target.value })} data-testid="ratei-ferie-initial" /></div>
                )}
                <div><Label className="text-xs">ROL residui iniziali (h, 3 decimali)</Label><CounterHoursInput value={form.ratei_rol_iniziale_h ?? 0} onCommit={(n) => setForm((f) => ({ ...f, ratei_rol_iniziale_h: n ?? 0 }))} data-testid="ratei-rol-initial" /></div>
                <div><Label className="text-xs">Ex festività residue iniziali (h, 3 decimali)</Label><CounterHoursInput value={form.ratei_ex_fest_iniziale_h ?? 0} onCommit={(n) => setForm((f) => ({ ...f, ratei_ex_fest_iniziale_h: n ?? 0 }))} data-testid="ratei-exfest-initial" /></div>
                <div><Label className="text-xs">Anno base per il riporto</Label><Input type="number" value={form.ratei_base_year || ""} onChange={(e) => setForm({ ...form, ratei_base_year: e.target.value ? Number(e.target.value) : null })} placeholder="es. 2026" data-testid="emp-form-ratei-base-year" /></div>
              </fieldset>

              <div className="mt-5 border-t border-suite-neutral-200 pt-4">
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div>
                    <div className="text-sm font-semibold text-suite-neutral-800">Maturazioni mensili storicizzate</div>
                    <div className="text-xs text-suite-neutral-500">
                      Ogni riga rappresenta una decorrenza. Nuove decorrenze non modificano i valori dei periodi precedenti.
                    </div>
                  </div>
                  {!isExternalConsultant && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={openNewAccrualDialog}
                      className="shrink-0 border-suite-primary/30 text-suite-primary hover:bg-suite-primary/5"
                      data-testid="new-accrual-btn"
                    >
                      <Plus size={14} className="mr-1.5" /> Aggiungi decorrenza
                    </Button>
                  )}
                </div>
                <div className="overflow-x-auto rounded-md border border-suite-neutral-200 bg-white">
                  <table className="w-full text-sm">
                    <thead className="bg-suite-neutral-50 border-b border-suite-neutral-200">
                      <tr className="text-[11px] uppercase tracking-wider text-suite-neutral-500">
                        <th className="px-3 py-2 text-left font-semibold">Dal</th>
                        <th className="px-3 py-2 text-left font-semibold">Ferie</th>
                        <th className="px-3 py-2 text-left font-semibold">ROL</th>
                        <th className="px-3 py-2 text-left font-semibold">Ex festività</th>
                        <th className="px-3 py-2 text-right font-semibold w-48">Azioni</th>
                      </tr>
                    </thead>
                    <tbody>
                      {accrualProfiles.length === 0 && (
                        <tr>
                          <td colSpan={5} className="px-3 py-6 text-center text-xs text-suite-neutral-500">
                            Nessuna decorrenza configurata.
                          </td>
                        </tr>
                      )}
                      {accrualProfiles.map((row) => (
                        <tr key={`${row.id || "legacy"}-${row.effective_from}`} className="border-t border-suite-neutral-100 hover:bg-suite-neutral-50/50 transition-colors">
                          <td className="px-3 py-2.5 font-mono text-[13px] text-suite-neutral-800">{fmtIsoIt(row.effective_from)}</td>
                          <td className="px-3 py-2.5 text-suite-neutral-700">
                            <span className="font-mono">{row.vacation_unit === "hours" ? fmtHours(row.vacation_monthly || 0) : Number(row.vacation_monthly || 0).toFixed(2)}</span>
                            <span className="text-[11px] text-suite-neutral-500 ml-1">{row.vacation_unit === "hours" ? "h" : "gg"}</span>
                          </td>
                          <td className="px-3 py-2.5 text-suite-neutral-700">
                            <span className="font-mono">{formatCounterHours(row.rol_monthly_hours || 0)}</span>
                            <span className="text-[11px] text-suite-neutral-500 ml-1">h</span>
                          </td>
                          <td className="px-3 py-2.5 text-suite-neutral-700">
                            <span className="font-mono">{formatCounterHours(row.ex_fest_monthly_hours || 0)}</span>
                            <span className="text-[11px] text-suite-neutral-500 ml-1">h</span>
                          </td>
                          <td className="px-3 py-2.5 text-right whitespace-nowrap">
                            <div className="inline-flex items-center gap-1">
                            {!isExternalConsultant && (
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => startAccrualEdit(row)}
                                className="h-8 px-2 text-suite-neutral-600 hover:text-suite-primary"
                                data-testid={`edit-accrual-${row.id || "legacy"}`}
                                title="Modifica decorrenza"
                              >
                                <Pencil size={13} className="mr-1" />
                                <span className="text-xs">Modifica</span>
                              </Button>
                            )}
                            {!isExternalConsultant && row.id && (
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => setDeletingAccrual(row)}
                                className="h-8 px-2 text-suite-neutral-600 hover:text-suite-error"
                                data-testid={`delete-accrual-${row.id}`}
                                title="Elimina decorrenza"
                              >
                                <Trash2 size={13} className="mr-1" />
                                <span className="text-xs">Elimina</span>
                              </Button>
                            )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          ) : (
            <div className="rounded-md border border-suite-primary/20 bg-suite-primary/5 px-3 py-2 text-xs text-suite-neutral-600">
              I contatori sono consultabili in sola lettura. Per eventuali rettifiche rivolgiti all&apos;azienda o allo studio.
            </div>
          )}

          <AccrualDeleteDialog
            employeeId={id}
            employeeName={`${form.last_name || ""} ${form.first_name || ""}`.trim()}
            row={deletingAccrual}
            onClose={closeAccrualDelete}
            onDeleted={afterAccrualDeleted}
            onCorrective={openCorrectiveAccrual}
          />

          {/* Dialog Nuova/Modifica decorrenza — formDialog: chiusura solo tramite pulsanti espliciti */}
          <Dialog open={accrualDialogOpen} onOpenChange={setAccrualDialogOpen}>
            <DialogContent formDialog className="max-w-xl" data-testid="accrual-dialog">
              <DialogHeader>
                <DialogTitle>{editingAccrualId ? "Modifica decorrenza" : "Nuova decorrenza"}</DialogTitle>
                <DialogDescription className="text-xs">
                  {editingAccrualId
                    ? "La rettifica aggiorna i calcoli dalla data indicata; le altre decorrenze restano invariate."
                    : "Inserisci i valori validi dalla nuova data, senza modificare i periodi precedenti."}
                </DialogDescription>
              </DialogHeader>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 py-2">
                <div className="sm:col-span-2">
                  <Label className="text-xs">Data di decorrenza</Label>
                  <Input type="date" value={newAccrual.effective_from}
                    onChange={(e) => setNewAccrual({ ...newAccrual, effective_from: e.target.value })}
                    data-testid="accrual-effective-from" />
                </div>
                <div>
                  {newAccrual.vacation_unit === "hours" ? (
                    <>
                      <DurationLabel className="text-xs" data-testid="accrual-ferie">Ferie mensili</DurationLabel>
                      <DurationInput value={newAccrual.vacation_monthly}
                        onCommit={(n) => setNewAccrual((f) => ({ ...f, vacation_monthly: n ?? 0 }))}
                        data-testid="accrual-ferie" />
                    </>
                  ) : (
                    <>
                      <Label className="text-xs">Ferie mensili</Label>
                      <Input type="number" min="0" step="0.01" value={newAccrual.vacation_monthly}
                        onChange={(e) => setNewAccrual({ ...newAccrual, vacation_monthly: e.target.value })}
                        data-testid="accrual-ferie" />
                    </>
                  )}
                </div>
                <div>
                  <Label className="text-xs">Unità ferie</Label>
                  <Select value={newAccrual.vacation_unit} onValueChange={(value) => setNewAccrual({ ...newAccrual, vacation_unit: value })}>
                    <SelectTrigger data-testid="accrual-ferie-unit"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="days">Giorni</SelectItem>
                      <SelectItem value="hours">Ore</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">ROL mensili (h, 3 decimali)</Label>
                  <CounterHoursInput value={newAccrual.rol_monthly_hours}
                    onCommit={(n) => setNewAccrual((f) => ({ ...f, rol_monthly_hours: n ?? 0 }))}
                    data-testid="accrual-rol" />
                </div>
                <div>
                  <Label className="text-xs">Ex festività mensili (h, 3 decimali)</Label>
                  <CounterHoursInput value={newAccrual.ex_fest_monthly_hours}
                    onCommit={(n) => setNewAccrual((f) => ({ ...f, ex_fest_monthly_hours: n ?? 0 }))}
                    data-testid="accrual-exfest" />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={cancelAccrualEdit} disabled={accrualBusy} data-testid="accrual-cancel-btn">
                  <X size={14} className="mr-1" /> Annulla
                </Button>
                <SuiteButton
                  onClick={editingAccrualId ? updateAccrualProfile : saveAccrualProfile}
                  icon={Save}
                  loading={accrualBusy}
                  data-testid="accrual-save-btn"
                >
                  {editingAccrualId ? "Salva modifica" : "Aggiungi decorrenza"}
                </SuiteButton>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </TabsContent>

        {/* === Sicurezza sul lavoro (D.Lgs. 81/2008) — Release 1.1.0 === */}
        {showSafetyTab && (
          <TabsContent value="sicurezza" className="mt-6 space-y-3">
            <EmployeeSafetyTab
              employeeId={id}
              employee={form}
              canManage={!isExternalConsultant && !isEmployeeSelf}
              isEmployeeSelf={isEmployeeSelf}
            />
          </TabsContent>
        )}


        {/* === Account portale === */}
        {showAccountTab && (
          <TabsContent value="account" className="mt-6 space-y-3">
            <div className="flex items-center gap-2 mb-1">
              <KeyRound size={16} className="text-suite-primary" />
              <h3 className="text-sm font-semibold text-suite-neutral-800">Account di accesso al portale</h3>
              {account.has_account ? (
                <div className="ml-auto flex items-center gap-2">
                  {account.role && account.role !== "employee" && (
                    <SuiteBadge tone="info" size="sm" testid="acc-role-management">Account aziendale</SuiteBadge>
                  )}
                  <SuiteBadge tone="ok" size="sm" testid="acc-status-active">Attivo</SuiteBadge>
                </div>
              ) : (
                <SuiteBadge tone="neutral" size="sm" className="ml-auto" testid="acc-status-none">Nessun account</SuiteBadge>
              )}
            </div>
            <p className="text-[11px] text-suite-neutral-500">
              {account.has_account
                ? "Il dipendente ha un accesso al portale. Puoi cambiare nome utente, email, generare una nuova password oppure disattivare l'accesso."
                : "Il dipendente non ha ancora un collegamento. Se nome utente ed email corrispondono a un account aziendale già esistente, verrà collegato senza duplicazioni e senza cambiarne il ruolo."}
            </p>
            <div className="mb-2 px-3 py-2 rounded-md bg-suite-warning-light/60 border border-suite-warning-border text-[11px] text-suite-warning">
              <strong>Attenzione:</strong> l&apos;account viene salvato con i pulsanti in questa scheda, indipendentemente da &quot;Salva modifiche&quot; del dipendente.
            </div>
            <div className="grid md:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Nome utente</Label>
                <Input value={accUsername} onChange={(e) => setAccUsername(e.target.value)}
                  placeholder="Generato come nome.cognome" data-testid="emp-acc-username" />
              </div>
              <div>
                <Label className="text-xs">
                  {account.has_account ? "Nuova password (lascia vuoto per non cambiarla)" : "Password iniziale (solo per un nuovo account)"}
                </Label>
                <div className="flex items-center gap-2">
                  <Input type="text" value={accPassword} onChange={(e) => setAccPassword(e.target.value)}
                    placeholder={account.has_account ? "•••••••• (invariata)" : "min. 6 caratteri"}
                    data-testid="emp-acc-password" />
                  <SuiteButton type="button" variant="secondary" size="sm" icon={Copy}
                    onClick={generateRandomPassword} data-testid="emp-acc-generate-pw">Genera</SuiteButton>
                </div>
              </div>
            </div>
            <div className="text-xs text-suite-neutral-600">
              Email per reset e notifiche: <strong>{form.email || "da inserire in Anagrafica"}</strong>
            </div>
            <div className="flex flex-wrap gap-2 mt-2">
              <SuiteButton type="button" size="sm" icon={Save}
                onClick={saveAccount} loading={accBusy} data-testid="emp-acc-save-btn">
                {account.has_account ? "Aggiorna account" : "Crea o collega account"}
              </SuiteButton>
              {account.has_account && (
                <SuiteButton type="button" size="sm" variant="secondary" icon={ShieldOff}
                  onClick={deleteAccount} loading={accBusy}
                  className="!text-suite-error !border-suite-error/40 hover:!bg-suite-error-light"
                  data-testid="emp-acc-delete-btn">Rimuovi accesso</SuiteButton>
              )}
            </div>
          </TabsContent>
        )}
      </Tabs>

      {/* Barra Salva sticky ancorata al TRUE bottom del contenitore di scroll (compensiamo il padding). */}
      {showSaveBar && (
        <div className="sticky -bottom-4 md:-bottom-6 lg:-bottom-8 z-30 mt-6 -mx-4 md:-mx-6 px-4 md:px-6 pt-3 pb-4 md:pb-6 lg:pb-8 border-t border-slate-200 bg-white shadow-[0_-2px_6px_rgba(0,0,0,0.04)] flex items-center justify-between">
          <div className="text-sm text-slate-500">
            {isDirty
              ? <span className="text-amber-700 font-medium" data-testid="employee-dirty-badge">Modifiche non salvate</span>
              : <span>Nessuna modifica in sospeso</span>}
          </div>
          <SuiteButton onClick={save} icon={Save} loading={saving} disabled={!isDirty} data-testid="employee-save-btn">
            Salva modifiche
          </SuiteButton>
        </div>
      )}
    </div>
  );
}
